import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MagnifyingGlassIcon, UserGroupIcon, UserCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Navbar from "../components/navBar";
import LifestyleCard from "../components/Lifestyle/LifestyleCard";
import axios from "axios";

function LifestylePage() {
  const [lifestyles, setLifestyles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const [isCollapsed, setIsCollapsed] = useState(
    () => typeof window !== "undefined" && window.localStorage.getItem("desac-sidebar-collapsed") === "true"
  );
  const fetchLifestyles = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/lifestyles`);
      const data = response.data;
      setLifestyles(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);
      setError(null);
    } catch (fetchError) {
      setError("We couldn't load the lifestyle collection. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLifestyles();
  }, [fetchLifestyles]);

  useEffect(() => {
    const updateViewport = () => {
      setIsDesktop(window.innerWidth >= 1024);
      setIsCollapsed(window.localStorage.getItem("desac-sidebar-collapsed") === "true");
    };
    const updateCollapsedState = (event) => {
      setIsCollapsed(
        typeof event?.detail?.collapsed === "boolean"
          ? event.detail.collapsed
          : window.localStorage.getItem("desac-sidebar-collapsed") === "true"
      );
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("storage", updateCollapsedState);
    window.addEventListener("desac-sidebar-toggle", updateCollapsedState);
    return () => {
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("storage", updateCollapsedState);
      window.removeEventListener("desac-sidebar-toggle", updateCollapsedState);
    };
  }, []);

  const filteredLifestyles = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return lifestyles.filter((lifestyle) => {
      const name = String(lifestyle?.name || "").toLowerCase();
      const description = String(lifestyle?.description || "").toLowerCase();
      return !query || name.includes(query) || description.includes(query);
    });
  }, [lifestyles, searchTerm]);

  const sidebarWidth = isDesktop && isCollapsed ? "5rem" : "16rem";
  const hasSearch = Boolean(searchTerm.trim());

  return (
    <>
      <Navbar />
      <main
        style={{ "--lifestyle-sidebar-width": sidebarWidth }}
        className="min-h-screen overflow-x-hidden bg-surface-950 pb-12 pt-16 text-white md:ml-64 md:pt-0 lg:ml-[var(--lifestyle-sidebar-width)]"
      >
        <header className="border-b border-line bg-surface-900">
          <div className="mx-auto flex max-w-7xl flex-col gap-6 px-5 py-8 sm:px-8 sm:py-10 lg:flex-row lg:items-end lg:justify-between lg:px-10">
            <div className="max-w-2xl">
              <p className="eyebrow-accent inline-flex items-center gap-2">
                <UserCircleIcon aria-hidden="true" className="h-4 w-4" />
                DESAC TV · COMMUNITY
              </p>
              <h1 className="mt-2 text-3xl font-bold tracking-tight text-white sm:text-4xl">Lifestyle</h1>
              <p className="mt-2 text-sm leading-6 text-gray-400 sm:text-base">
                Discover the people in our lifestyle collection and learn more about their profiles.
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center lg:w-[min(100%,32rem)] lg:justify-end">
              {!loading && !error && (
                <div aria-live="polite" className="inline-flex min-h-10 w-fit shrink-0 items-center gap-2 rounded-lg border border-line bg-surface-850 px-3 text-sm text-gray-300">
                  <UserGroupIcon aria-hidden="true" className="h-4 w-4 text-accent-soft" />
                  {filteredLifestyles.length} {filteredLifestyles.length === 1 ? "profile" : "profiles"}
                </div>
              )}
              <label className="relative block min-w-0 flex-1">
                <span className="sr-only">Search lifestyle profiles</span>
                <MagnifyingGlassIcon aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="search"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder="Search profiles"
                  className="field min-h-11 pl-10 pr-10 text-sm"
                />
                {searchTerm && (
                  <button
                    type="button"
                    aria-label="Clear search"
                    onClick={() => setSearchTerm("")}
                    className="absolute right-2 top-1/2 inline-flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-surface-700 hover:text-white"
                  >
                    <XMarkIcon aria-hidden="true" className="h-4 w-4" />
                  </button>
                )}
              </label>
            </div>
          </div>
        </header>

        <div className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8 lg:px-10">
          {loading ? (
            <div role="status" aria-label="Loading lifestyle profiles" className="flex min-h-72 items-center justify-center gap-3 rounded-xl border border-line bg-surface-900 text-sm text-gray-300">
              <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-surface-600 border-t-accent" />
              Loading lifestyle profiles…
            </div>
          ) : error ? (
            <section role="alert" className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center sm:px-8">
              <UserCircleIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">Lifestyle is temporarily unavailable</h2>
              <p className="mt-2 text-sm text-gray-400">{error}</p>
              <button type="button" onClick={fetchLifestyles} className="btn-primary mt-5 min-h-11">Try again</button>
            </section>
          ) : filteredLifestyles.length === 0 ? (
            <section className="rounded-xl border border-line bg-surface-900 px-5 py-12 text-center sm:px-8">
              <MagnifyingGlassIcon aria-hidden="true" className="mx-auto h-9 w-9 text-gray-500" />
              <h2 className="mt-4 text-lg font-semibold text-white">
                {lifestyles.length ? "No profiles match your search" : "No profiles available yet"}
              </h2>
              <p className="mt-2 text-sm text-gray-400">
                {lifestyles.length
                  ? "Try another name or search for a different profile detail."
                  : "Lifestyle profiles will appear here when they are available."}
              </p>
              {hasSearch && (
                <button type="button" onClick={() => setSearchTerm("")} className="btn-secondary mt-5 min-h-11">
                  Clear search
                </button>
              )}
            </section>
          ) : (
            <>
              <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
                <div>
                  <p className="eyebrow">THE COLLECTION</p>
                  <h2 className="mt-1 text-xl font-semibold tracking-tight text-white">Lifestyle profiles</h2>
                </div>
                <p className="text-xs text-gray-500" aria-live="polite">
                  Showing {filteredLifestyles.length} of {lifestyles.length} {lifestyles.length === 1 ? "profile" : "profiles"}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {filteredLifestyles.map((lifestyle, index) => (
                  <LifestyleCard
                    key={lifestyle.id ?? `${lifestyle.name || "profile"}-${index}`}
                    lifestyle={lifestyle}
                    isAdmin={false}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </main>
    </>
  );
}

export default LifestylePage;
